import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError, brl, imgUrl, effectivePrice, listPrice, hasDiscount, rows, pageMeta } from "@/lib/api";
import { descricaoParaHtml } from "@/lib/richText";
import { youtubeEmbedUrl, youtubeId } from "@/lib/youtube";
import { Thumb } from "@/components/ui-kit";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { ShoppingCart, Check, AlertTriangle, MessageCircle, Star, PlayCircle } from "lucide-react";
import { toast } from "sonner";

function Stars({ value, onChange }) {
  return (
    <span className="flex text-amber-400">
      {Array.from({ length: 5 }, (_, i) => (
        <button key={i} type="button" onClick={onChange ? () => onChange(i + 1) : undefined} data-testid={onChange ? `review-star-${i + 1}` : undefined} className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star size={onChange ? 20 : 14} fill={i < value ? "currentColor" : "none"} />
        </button>
      ))}
    </span>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [reviewsMeta, setReviewsMeta] = useState(null);
  const [whatsapp, setWhatsapp] = useState("");
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });

  useEffect(() => {
    setProduct(null); setError(false);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setVariant(r.data.variants.find((v) => v.stock > 0) || r.data.variants[0]);
      setActiveImg(r.data.image || r.data.images?.[0]?.url || null);
    }).catch(() => setError(true));
    setReviews([]); setReviewsMeta(null);
    api.get(`/products/${slug}/reviews`)
      .then((r) => { setReviews(rows(r)); setReviewsMeta(pageMeta(r)); })
      .catch(() => {});
    api.get("/settings").then((r) => setWhatsapp(r.data.whatsapp)).catch(() => {});
  }, [slug]);

  const loadMoreReviews = async () => {
    const next = (reviewsMeta?.page || 1) + 1;
    try {
      const r = await api.get(`/products/${slug}/reviews`, { params: { page: next } });
      setReviews((prev) => [...prev, ...rows(r)]);
      setReviewsMeta(pageMeta(r));
    } catch { /* silencioso, igual ao carregamento inicial */ }
  };

  if (error) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="product-detail-error">
      <p className="text-lg font-semibold text-slate-900">Produto não encontrado</p>
      <p className="text-sm text-slate-500 mt-1">Ele pode ter saído do catálogo ou o link está incorreto.</p>
      <Link to="/produtos" className="inline-block mt-5 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-lg">Ver todos os produtos</Link>
    </div>
  );
  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">Carregando...</div>;

  // `null` quando não há vídeo ou o link não é do YouTube — nesse caso o bloco
  // some, em vez de mostrar um player quebrado.
  const videoId = youtubeId(product.video_url);

  const price = effectivePrice(product, variant);
  const listP = listPrice(product, variant);
  const onSale = hasDiscount(product);
  const waLink = `https://wa.me/${whatsapp}?text=Olá! Tenho uma dúvida sobre o produto: ${product.name}`;

  const submitReview = async () => {
    if (!reviewForm.comment) { toast.error("Escreva um comentário"); return; }
    try {
      await api.post("/reviews", { product_id: product.id, ...reviewForm });
      toast.success("Avaliação enviada! Será publicada após moderação.");
      setReviewForm({ rating: 5, comment: "" });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="h-fit">
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          {/* `object-contain`, e não `object-cover`: a moldura é quadrada, mas a
              foto não precisa ser. Com `cover` uma tabela de medidas em retrato
              — a de dimensões da cadeira de rodas, por exemplo — perdia o topo
              e o pé, justamente as linhas que a pessoa abriu a foto para ler.
              A moldura continua quadrada para a galeria não pular de altura ao
              trocar de foto; o que sobra dos lados fica branco. */}
            <Thumb src={activeImg || product.image} alt={product.name} iconSize={44} className="w-full aspect-square object-contain" data-testid="product-detail-image" />
          </div>
          {(product.images?.length || 0) > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap" data-testid="product-detail-gallery">
              {product.images.map((im, i) => (
                <button key={im.id || i} type="button" onClick={() => setActiveImg(im.url)}
                  aria-label={`Ver foto ${i + 1} de ${product.name}`}
                  aria-pressed={(activeImg || product.image) === im.url}
                  className={`w-16 h-16 rounded-md overflow-hidden border transition-colors ${(activeImg || product.image) === im.url ? "border-brand-600" : "border-slate-200 hover:border-slate-400"}`}>
                  <img src={imgUrl(im.url)} alt="" className="w-full h-full object-contain bg-white" />
                </button>
              ))}
            </div>
          )}
          {/* O vídeo mora na coluna das fotos, não abaixo da descrição: a
              descrição já é longa, e a coluna da esquerda termina na galeria
              com a página inteira sobrando embaixo. Aqui ele aparece ao lado
              do texto, sem esticar a página. Tem título para não ser um
              vídeo solto na tela — e o título diz de que é. */}
          {videoId && (
            <section className="mt-8" data-testid="product-detail-video" aria-labelledby="product-video-title">
              <h3 id="product-video-title" className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <PlayCircle size={18} className="text-brand-600" aria-hidden />
                Veja o produto em vídeo
              </h3>
              <p className="text-sm text-slate-500 mb-3">{product.name}</p>
              <div className="aspect-video rounded-xl overflow-hidden border border-slate-200 bg-slate-900">
                {/* `loading="lazy"`: o iframe do YouTube pesa mais que a página
                    inteira, e só precisa carregar se a pessoa rolar até ele. */}
                <iframe
                  src={youtubeEmbedUrl(videoId)}
                  title={`Vídeo: ${product.name}`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </section>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-brand-600 mb-2">{product.category_slug?.replace(/-/g, " ")}</p>
          <h1 className="font-heading text-3xl font-semibold text-slate-900" data-testid="product-detail-name">{product.name}</h1>
          {reviews.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              {/* A média vem do servidor (meta.rating_avg): calcular aqui daria a
                  média só das avaliações já carregadas. */}
              <Stars value={Math.round(reviewsMeta?.rating_avg ?? 0)} />
              {reviewsMeta?.rating_avg != null && (
                <span className="text-sm font-semibold text-slate-700">{reviewsMeta.rating_avg.toFixed(1)}</span>
              )}
              <span className="text-xs text-slate-500">({reviewsMeta?.total ?? reviews.length} avaliações)</span>
            </div>
          )}
          {onSale ? (
            <div className="mt-4">
              <span className="text-base text-slate-400 line-through mr-2" data-testid="product-detail-list-price">{brl(listP)}</span>
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 rounded px-1.5 py-0.5 align-middle">-{Math.round((1 - price / listP) * 100)}%</span>
              <p className="font-heading text-3xl font-semibold text-emerald-600" data-testid="product-detail-price">{brl(price)}</p>
            </div>
          ) : (
            <p className="font-heading text-3xl font-semibold text-slate-900 mt-4" data-testid="product-detail-price">{brl(price)}</p>
          )}
          <p className="text-sm text-slate-500 mt-1">ou 12x de {brl(price / 12)} no cartão · PIX com 5% off</p>

          <div className="mt-6">
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">Variação</p>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v) => (
                <button key={v.id} onClick={() => setVariant(v)} disabled={v.stock === 0}
                  data-testid={`variant-btn-${v.id}`}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    variant?.id === v.id ? "border-brand-600 bg-brand-50 text-brand-700" : "border-slate-200 text-slate-700 hover:border-slate-400"
                  } ${v.stock === 0 ? "opacity-40 line-through" : ""}`}>
                  {v.label}
                </button>
              ))}
            </div>
            {variant && (
              <p className="mt-2 text-sm flex items-center gap-1" data-testid="variant-stock-info">
                {variant.stock > 5 ? <><Check size={14} className="text-emerald-500" /><span className="text-emerald-600">Em estoque</span></>
                  : variant.stock > 0 ? <><AlertTriangle size={14} className="text-amber-500" /><span className="text-amber-600">Últimas {variant.stock_online} unidades</span></>
                  : <span className="text-red-500">Sem estoque</span>}
              </p>
            )}
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-slate-200 rounded-lg">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease-btn" className="px-3 py-2 text-slate-600">−</button>
              <span className="w-8 text-center text-sm font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(variant?.stock || 1, qty + 1))} disabled={qty >= (variant?.stock || 1)}
                data-testid="qty-increase-btn" className="px-3 py-2 text-slate-600 disabled:opacity-30">+</button>
            </div>
            <button
              onClick={() => { addItem(product, variant, qty); toast.success("Adicionado ao carrinho"); }}
              disabled={!variant || variant.stock === 0}
              data-testid="add-to-cart-btn"
              className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors">
              <ShoppingCart size={18} /> Adicionar ao carrinho
            </button>
          </div>

          <a href={waLink} target="_blank" rel="noreferrer" data-testid="product-whatsapp-btn"
            className="mt-3 w-full border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors">
            <MessageCircle size={18} /> Dúvida? Chama no WhatsApp
          </a>

          <div className="mt-8 border-t border-slate-200 pt-6">
            <h3 className="font-semibold text-slate-900 mb-2">Descrição</h3>
            {/* A descrição vem em HTML do editor do cadastro (ou em texto puro,
                de produto antigo — `descricaoParaHtml` converte). O `.rich-text`
                é o mesmo CSS do editor: o que o admin vê ao escrever é o que o
                cliente lê aqui. E é limpo com DOMPurify antes de entrar no DOM,
                mesmo já saindo limpo do servidor — a loja não pode depender de
                o banco estar limpo. */}
            <div
              className="rich-text text-slate-600 leading-relaxed"
              data-testid="product-detail-description"
              dangerouslySetInnerHTML={{ __html: descricaoParaHtml(product.description) }}
            />
          </div>
        </div>
      </div>

      <section className="mt-14 max-w-3xl">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-5">Avaliações</h2>
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid="review-card">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 text-sm">{r.user_name}</span>
                <Stars value={r.rating} />
                <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("pt-BR")}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1.5">{r.comment}</p>
            </div>
          ))}
          {reviews.length === 0 && <p className="text-sm text-slate-400">Nenhuma avaliação publicada ainda.</p>}
          {reviewsMeta?.has_more && (
            <button type="button" onClick={loadMoreReviews} data-testid="reviews-load-more"
              className="w-full border border-slate-200 rounded-lg py-2.5 text-sm font-medium text-slate-600 hover:border-brand-500 hover:text-brand-600 transition-colors">
              Carregar mais avaliações ({reviews.length} de {reviewsMeta.total})
            </button>
          )}
        </div>
        {user ? (
          <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="review-form">
            <p className="font-semibold text-sm text-slate-900 mb-3">Deixe sua avaliação</p>
            <Stars value={reviewForm.rating} onChange={(v) => setReviewForm({ ...reviewForm, rating: v })} />
            <textarea value={reviewForm.comment} aria-label="Seu comentário sobre o produto" onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
              rows={3} placeholder="O que achou do produto?" data-testid="review-comment-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-3" />
            <button onClick={submitReview} data-testid="review-submit-btn"
              className="mt-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-5 py-2 rounded-lg">Enviar avaliação</button>
          </div>
        ) : (
          <p className="mt-4 text-sm text-slate-500">Faça login para avaliar este produto.</p>
        )}
      </section>
    </div>
  );
}
